import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ManSingleWrestlerRenderPage from '../../components/MobileComponents/SingleWrestlerPage/ManSingleWrestlerRenderPage';
import { useParams } from 'react-router-dom';
import { LoadingState } from '../../components/stats_components/LoadingState';
import type { RootState } from '../../types';
import type { User } from '../../types';
import type { WrestlerWithStats } from '../../types';
import type { Card } from '../../types/models/card';
import { apiGetWrestlerCards } from '../../api/cardApi';

function SingleWrestlerMobilePage() {
    const { user } = useSelector((state: RootState) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [wrestlerData, setWrestlerData] = useState<WrestlerWithStats | null>(null);
    const [wrestlerCards, setWrestlerCards] = useState<Card[]>([]);
    const [isLoadingCards, setIsLoadingCards] = useState(true);
    const params = useParams<{ idString: string }>();
    const [isAlboDoroOpen, setIsAlboDoroOpen] = useState(true);
    const [isInfoPageOpen, setIsInfoPageOpen] = useState(false);
    const [isPrimePageOpen, setIsPrimePageOpen] = useState(false);
    const [isArchivioPageOpen, setIsArchivioPageOpen] = useState(false);
    const [isNemiciAmiciPageOpen, setIsNemiciAmiciPageOpen] = useState(false);
    const [isTopMatchPageOpen, setIsTopMatchPageOpen] = useState(false);

    // Funzione unificata per resettare tutti gli stati e impostare quello selezionato
    const resetAllStates = (setActiveState: (value: boolean) => void) => {
        setIsAlboDoroOpen(false);
        setIsInfoPageOpen(false);
        setIsPrimePageOpen(false);
        setIsArchivioPageOpen(false);
        setIsNemiciAmiciPageOpen(false);
        setIsTopMatchPageOpen(false);
        setActiveState(true);
    };

    // Funzioni di gestione click - molto più pulite
    const handleClickAlboDoroOpen = () => resetAllStates(setIsAlboDoroOpen);
    const handleClickInfoPageOpen = () => resetAllStates(setIsInfoPageOpen);
    const handleClickPrimePageOpen = () => resetAllStates(setIsPrimePageOpen);
    const handleClickArchivioPageOpen = () => resetAllStates(setIsArchivioPageOpen);
    const handleClickNemiciAmiciPageOpen = () => resetAllStates(setIsNemiciAmiciPageOpen);
    const handleClickTopMatchPageOpen = () => resetAllStates(setIsTopMatchPageOpen);

    const refreshWrestlerData = useCallback(async (): Promise<void> => {
        if (!params.idString) return;
        try {
            const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/wrestlers/' + params.idString);
            if (response.ok) {
                const data = await response.json();
                if (data && typeof data === 'object' && 'id' in data) {
                    setWrestlerData(data);
                }
            }
        } catch (error) {
            console.error('Errore nel refresh dei dati del wrestler:', error);
        }
    }, [params.idString]);

    // Appenna atterro qui, carico i dati. Ignoro i dati del loader
    useEffect(() => {
        if (!params.idString) return;
        const controller = new AbortController();
        const loadWrestlerData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/wrestlers/' + params.idString, { signal: controller.signal });
                if (response.ok) {
                    const data = await response.json();
                    setWrestlerData(data);
                }
            } catch (error: unknown) {
                if (error instanceof Error && error.name !== 'AbortError') {
                    console.error('Errore nel caricamento del wrestler:', error);
                }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };
        loadWrestlerData();
        return () => controller.abort();
    }, [params.idString]);

    useEffect(() => {
        if (!wrestlerData?.id) return;
        const controller = new AbortController();
        setIsLoadingCards(true);
        apiGetWrestlerCards(wrestlerData.id)
            .then(res => { if (!controller.signal.aborted) setWrestlerCards(res.data ?? []); })
            .catch(() => { if (!controller.signal.aborted) setWrestlerCards([]); })
            .finally(() => { if (!controller.signal.aborted) setIsLoadingCards(false); });
        return () => controller.abort();
    }, [wrestlerData?.id]);

    // Se è in loading, mostro il loading state
    if (isLoading) {
        return <LoadingState />;
    }

    // Se non ci sono dati, mostra un messaggio di errore (evita di passare null ai figli)
    if (wrestlerData == null || typeof wrestlerData !== 'object' || !('id' in wrestlerData)) {
        return <div>Nessun dato trovato per questo wrestler</div>;
    }

    return (
        <ManSingleWrestlerRenderPage
            wrestlerData={wrestlerData}
            user={user as User | null}
            onWrestlerDataUpdate={refreshWrestlerData}
            wrestlerCards={wrestlerCards}
            isLoadingCards={isLoadingCards}
            isAlboDoroOpen={isAlboDoroOpen}
            isInfoPageOpen={isInfoPageOpen}
            isPrimePageOpen={isPrimePageOpen}
            isArchivioPageOpen={isArchivioPageOpen}
            isNemiciAmiciPageOpen={isNemiciAmiciPageOpen}
            isTopMatchPageOpen={isTopMatchPageOpen}
            handleClickAlboDoroOpen={handleClickAlboDoroOpen}
            handleClickInfoPageOpen={handleClickInfoPageOpen}
            handleClickPrimePageOpen={handleClickPrimePageOpen}
            handleClickArchivioPageOpen={handleClickArchivioPageOpen}
            handleClickNemiciAmiciPageOpen={handleClickNemiciAmiciPageOpen}
            handleClickTopMatchPageOpen={handleClickTopMatchPageOpen}
        />
    );
}


export default SingleWrestlerMobilePage;